import { Select } from 'antd';
import moment from 'moment';
import React, { useMemo } from 'react';
import styled, { useTheme } from 'styled-components';
import { Button } from '../../components/Button/Button';
import { sortByHighestSeverity } from '../../logic/store/Insights/InsightsModels';
import { useAppSelector } from '../../logic/store/Store';
import { ReportView } from './Report';
import { SingleRow } from './SingleRow';
import { FeedbackThumbs } from './Feedback';
import { ArrowBackIcon } from './Icons/ArrowBackIcon';
import { SectionHeader } from './Components/SectionHeader';

const StyledDateRowContainer = styled.div`
	display: flex;
	flex-direction: row;
	align-items: center;
	gap: 12px;

	// mobile
	@media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
		.ant-select {
			width: 100% !important;
		}
	}
`;

interface ISingleTerritoryViewProps
{
	onSelectSalesRep: (Id: string) => void;
	changeSnapshotDate: (Id: string, snapshotDate: string) => void;
	backToLastView: () => void;
	/** False when the user only has one territory, so there is no list to go back to. */
	canGoBack: boolean;
}

export const SingleTerritoryView = (props: ISingleTerritoryViewProps) =>
{
	const theme = useTheme();

	const { backToLastView, onSelectSalesRep, changeSnapshotDate, canGoBack } = props;

	const selectedTerritoryId = useAppSelector(s => s.insights.currentTerritoryId);
	const territoryAnalysis = useAppSelector(s => s.insights.singleTerritoryAnalysis);
	const selectedSnapshotDate = territoryAnalysis?.SnapshotDate;

	// Sort the sales reps by severity
	const sortedSalesReps = useMemo(() =>
	{
		if (!territoryAnalysis || territoryAnalysis.SalesRepAnalyses == null || territoryAnalysis.SalesRepAnalyses.length === 0)
		{
			return [];
		}

		return sortByHighestSeverity(territoryAnalysis.SalesRepAnalyses, s => s.Report?.Concerns || [], s => s.SalesRepName);
	}, [territoryAnalysis]);

	// Change the report's snapshot date
	const onChangeSnapshotDate = (date: string) =>
	{
		if (!date)
		{
			return;
		}

		changeSnapshotDate(selectedTerritoryId, date);
	};

	const startYear = (new Date().getFullYear()-1).toString();
	const availableDates = territoryAnalysis?.AvailableDates?.filter(ad => ad > startYear).sort().reverse() || [];

	const managerNames = territoryAnalysis?.Managers?.map(m => m.Name).filter(n => !!n).join(', ');

	return (
		<div 
			style={{ 
				display: 'flex', 
				flexDirection: 'column',
				width: '100%', 
				height: '98%', 
				overflow: 'hidden',
			}}>
			<SectionHeader>
				{
					canGoBack && (
						<Button 
							variant='light' 
							style={{
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'center',
								height: 32,
								width: 32,
							}}
							title='Back to Territory List'
							onClick={backToLastView}
						>
							<ArrowBackIcon style={{transform: 'translateY(-1px)'}} />
						</Button>
					)}
					Territory - {territoryAnalysis?.TerritoryName ?? territoryAnalysis?.TerritoryExternalId}
			</SectionHeader>
			<div style={{ 
				display: 'flex', 
				overflowY: 'auto', 
				flexDirection: 'column', 
				padding: '20px 0px',
			}}>
				<StyledDateRowContainer>
					<span style={{ fontWeight: theme.fontWeights.bold, minWidth: 90 }}>Report Date:</span>
					<Select value={selectedSnapshotDate} style={{ width: 150 }} onChange={onChangeSnapshotDate}>
						{
							availableDates.map(date => (
								<Select.Option key={date} value={date}>
									{moment(date, 'YYYY-MM-DD').format('L')}
								</Select.Option>
							))
						}
					</Select>
				</StyledDateRowContainer>
				{
					managerNames && (
						<div style={{ display: 'flex', flexDirection: 'row', marginTop: 10, gap: 6 }}>
							<span style={{ fontWeight: theme.fontWeights.bold, minWidth: 90 }}>Managers:</span>
							<div style={{ marginLeft: 10 }}>{managerNames}</div>
						</div>
					)}
				<div style={{ width: '95%', display: 'flex', flexDirection: 'row', marginTop: 10, marginBottom: 20, gap: 6 }}>
					<div style={{ display: 'flex', flexDirection: 'column', minWidth: 90 }}>
						<div style={{ fontWeight: theme.fontWeights.bold, marginBottom: 5 }}>
							Overview:
						</div>
						<FeedbackThumbs report={territoryAnalysis?.Report} />
					</div>
					<div style={{ marginLeft: 10 }}>{territoryAnalysis?.Report?.Overview}</div>
				</div>
				<ReportView report={territoryAnalysis?.Report} />
				{
					sortedSalesReps?.length > 0 && (
						<div style={{ marginTop: 20 }}>
							<SectionHeader>
								Sales Reps
							</SectionHeader>
							<div style={{ display: 'flex',  flexDirection: 'column', alignItems: 'center', paddingBottom: 20 }}>
								{
									sortedSalesReps?.map((salesRep) =>
									{
										return (
											<SingleRow
												key={JSON.stringify([salesRep.item.SalesRepUserId, salesRep.item.RelationshipTitle])}
												Id={salesRep.item.SalesRepUserId}
												onSelect={(Id) => onSelectSalesRep(Id)}
												Name={salesRep.item.SalesRepName}
												RelationshipTitle={salesRep.item.RelationshipTitle}
												NumberOfItems={salesRep.item.SellerAnalyses?.length}
												NumberOfItemsTitle={'Sellers'}
												Severity={salesRep.highestSeverity}
												SeverityCount={salesRep.count}
											/>
										);
									})
								}
							</div>
						</div>
					)}
			</div>
		</div>
	);
};
